/**
 * Arrow end-cap geometry/sizing.
 *
 * The chevron is built as a TRUE V on the ground (lat-corrected lng/lat — no screen
 * projection), so it behaves like every other markup: it tilts and pans WITH the map
 * (foreshortening naturally under tilt, never morphing mid-gesture) and is symmetric on
 * the ground. The dot is a Mapbox circle (always round on screen, so it can't distort
 * into an ellipse under tilt).
 *
 * Cap SIZE = min(thickness term, length term):
 *  - thickness term = stroke thickness × factor × zoom-scale → tracks line weight + zoom.
 *  - length term = a fraction of the arrow's GEOGRAPHIC length (px at the current zoom)
 *    → stops the caps from overwhelming a short arrow (which would read as a blob).
 * Both terms depend only on stroke width, lng/lat, zoom and latitude — never the
 * projected screen distance — so tilt/bearing/rotation/pan never change cap size; only
 * zoom (or editing the geometry) does. Callers re-fit the caps on zoom, not on every
 * move, so size updates with zoom while the chevron tilts freely with the map.
 */
const HALF_ANGLE_DEG = 33

// Cap base size = stroke thickness × factor (screen px) at the reference zoom.
export const DOT_RADIUS_FACTOR = 2.5
export const CHEVRON_WING_FACTOR = 6

// Length term: cap each end at this fraction of the terminal segment's on-screen geo
// length, so a short arrow keeps proportional (arrow-shaped) caps. Bites only when the
// arrow is short relative to its thickness; long arrows stay thickness-driven.
const DOT_LEN_CAP = 0.15
const CHEVRON_LEN_CAP = 0.25

// Zoom → size multiplier. At CAP_REF_ZOOM the multiplier is 1; it doubles per zoom
// level in and halves per level out, clamped so caps stay sane at zoom extremes.
// The floor must keep the dot bigger than the line's own round end-cap (radius =
// strokeWidth/2): at scale s the dot radius is strokeWidth × DOT_RADIUS_FACTOR × s, so
// s must stay above 0.5/DOT_RADIUS_FACTOR (= 0.2 here) or the dot vanishes into the
// line end. 0.45 keeps it a clear knob (~2.25× the line cap) when zoomed out.
const CAP_REF_ZOOM = 19
const CAP_MIN_SCALE = 0.45
const CAP_MAX_SCALE = 3

// Web-mercator constants (Mapbox GL uses a 512px world tile).
const TILE_SIZE = 512
const EARTH_CIRCUMFERENCE_M = 40075016.6856
const EARTH_RADIUS_M = 6378137

function rotate(x: number, y: number, angleRad: number): [number, number] {
  const cos = Math.cos(angleRad)
  const sin = Math.sin(angleRad)
  return [x * cos - y * sin, x * sin + y * cos]
}

function capZoomScale(zoom: number): number {
  return Math.min(
    CAP_MAX_SCALE,
    Math.max(CAP_MIN_SCALE, 2 ** (zoom - CAP_REF_ZOOM)),
  )
}

// The terminal segment's ground length expressed in screen px at the given zoom — what
// the segment WOULD span on a flat (top-down) map. Independent of tilt/bearing/pan, so
// using it to bound cap size never makes the cap wobble during those gestures.
function segmentGeoPx(prev: number[], tip: number[], zoom: number): number {
  const meanLatRad = (((prev[1] + tip[1]) / 2) * Math.PI) / 180
  const dLatRad = ((tip[1] - prev[1]) * Math.PI) / 180
  const dLngRad = ((tip[0] - prev[0]) * Math.PI) / 180
  const meters =
    EARTH_RADIUS_M * Math.hypot(dLngRad * Math.cos(meanLatRad), dLatRad)
  const metersPerPx =
    (EARTH_CIRCUMFERENCE_M * Math.cos(meanLatRad)) / (TILE_SIZE * 2 ** zoom)
  return metersPerPx > 0 ? meters / metersPerPx : 0
}

// Chevron wing length (screen px at the given zoom): thickness/zoom-driven, but never
// more than a fraction of the arrow's on-screen length (so a short arrow stays
// arrow-shaped, not a blob).
export function chevronWingPx(
  strokeWidth: number,
  prev: number[],
  tip: number[],
  zoom: number,
): number {
  const thickness = strokeWidth * CHEVRON_WING_FACTOR * capZoomScale(zoom)
  return Math.min(thickness, segmentGeoPx(prev, tip, zoom) * CHEVRON_LEN_CAP)
}

// Dot radius (screen px): thickness/zoom-driven, length-capped, balanced vs the chevron.
export function dotRadiusPx(
  strokeWidth: number,
  prev: number[],
  tip: number[],
  zoom: number,
): number {
  const thickness = strokeWidth * DOT_RADIUS_FACTOR * capZoomScale(zoom)
  return Math.min(thickness, segmentGeoPx(prev, tip, zoom) * DOT_LEN_CAP)
}

// Open-chevron cap as a `[leftWing, tip, rightWing]` LineString in lng/lat — a true
// symmetric V built in lat-corrected space so the ±angle reads true on the map.
export function arrowheadChevron(
  prev: number[],
  tip: number[],
  wingPx: number,
  zoom: number,
): number[][] | null {
  if (wingPx <= 0) return null
  const cosLat = Math.cos((tip[1] * Math.PI) / 180) || 1
  const dx = (tip[0] - prev[0]) * cosLat
  const dy = tip[1] - prev[1]
  const length = Math.hypot(dx, dy)
  if (!length) return null
  const ux = dx / length
  const uy = dy / length
  // px → lat-corrected degrees at this zoom (web-mercator, 512px world).
  const wingDeg = (wingPx * 360 * cosLat) / (TILE_SIZE * 2 ** zoom)
  const angle = (HALF_ANGLE_DEG * Math.PI) / 180
  const [lx, ly] = rotate(-ux, -uy, angle)
  const [rx, ry] = rotate(-ux, -uy, -angle)
  const toLngLat = (ox: number, oy: number): number[] => [
    tip[0] + (ox * wingDeg) / cosLat,
    tip[1] + oy * wingDeg,
  ]
  return [toLngLat(lx, ly), tip, toLngLat(rx, ry)]
}
